import type { ChangeEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useTracking } from '@change-corgi/core-react/tracking';

import {
	useContributionsPageState,
	useContributionsPageData,
} from '#src/app/pages/accountSettings/contributions/main/pageContext';
import { convertToUsdNumber } from '#src/app/shared/utils/currency';
import { localizeCustomAmount, parseLocalizedDecimalAmount } from '#src/app/shared/utils/payments';

type Result = ModelHookResult<
	{
		customAmount: string;
	},
	{
		handleCustomAmountSelect: () => void;
		handleCustomAmountFocus: () => void;
		handleCustomAmountChange: (value: ChangeEvent<HTMLInputElement>) => void;
	}
>;

export function useCustomAmount(): Result {
	const { data } = useContributionsPageData();
	const [
		{
			subscription: {
				config: { amountChoices },
				unsavedAmount,
				currencyCode: subsCurrencyCode,
			},
		},
		{ setContributionAmount },
	] = useContributionsPageState();
	const track = useTracking();
	const { localizeNumber, getDecimalSeparator } = useI18n();

	const derivedCustomAmount = useMemo(
		() => (amountChoices.some((amount) => amount === unsavedAmount) ? 0 : unsavedAmount),
		[amountChoices, unsavedAmount],
	);

	const [customAmount, setCustomAmount] = useState<number>(derivedCustomAmount);

	const [lastSyncedDerivedAmount, setLastSyncedDerivedAmount] = useState<number>(derivedCustomAmount);
	if (derivedCustomAmount !== lastSyncedDerivedAmount) {
		setLastSyncedDerivedAmount(derivedCustomAmount);
		if (derivedCustomAmount > 0) {
			setCustomAmount(derivedCustomAmount);
		}
	}

	const handleCustomAmountFocus = useCallback((): void => {
		void track('subs_settings_change_custom_amount_focus');
	}, [track]);

	const handleCustomAmountChange = useCallback(
		(value: ChangeEvent<HTMLInputElement>): void => {
			const result = parseLocalizedDecimalAmount(value.target.value, getDecimalSeparator());
			if (!result.isValid) return;
			setCustomAmount(result.integerPart);
			setContributionAmount(result.integerPart);
			void track('subs_settings_change_amount_changed', {
				local_amount: result.integerPart,
				amount: convertToUsdNumber(result.integerPart, data.currencyExchangeRate ?? 1, subsCurrencyCode),
			});
		},
		[setContributionAmount, track, data.currencyExchangeRate, subsCurrencyCode, getDecimalSeparator],
	);

	const handleCustomAmountSelect = useCallback((): void => {
		void track('subs_settings_change_amount_other_click');
		setContributionAmount(customAmount);
	}, [setContributionAmount, customAmount, track]);

	return {
		data: {
			customAmount: localizeCustomAmount(customAmount, localizeNumber),
		},
		actions: {
			handleCustomAmountSelect,
			handleCustomAmountFocus,
			handleCustomAmountChange,
		},
	};
}
